'use client';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import type { LabelColor, LabelVariant } from './types';

interface StyledLabelProps {
  ownerState: {
    color: LabelColor;
    variant: LabelVariant;
  };
}

const pillColors: Record<string, { text: string; bg: string; border: string }> = {
  default:   { text: 'var(--text-tertiary)',  bg: 'var(--bg-app)',     border: 'var(--line-strong)' },
  primary:   { text: 'var(--accent-text)',    bg: 'var(--accent-bg)',  border: 'var(--accent-border)' },
  secondary: { text: 'var(--accent-text)',    bg: 'var(--accent-bg)',  border: 'var(--accent-border)' },
  info:      { text: 'var(--accent-text)',    bg: 'var(--accent-bg)',  border: 'var(--accent-border)' },
  success:   { text: 'var(--success-text)',   bg: 'var(--success-bg)', border: 'var(--success-border)' },
  warning:   { text: 'var(--yellow-text)',    bg: 'var(--yellow-bg)',  border: 'var(--yellow-border)' },
  error:     { text: 'var(--danger-text)',    bg: 'var(--danger-bg)',  border: 'var(--danger-border)' },
};

export const StyledLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<StyledLabelProps>(({ theme, ownerState: { color, variant } }) => {
  const tokens = pillColors[color] ?? pillColors.default;

  const colorStyles = {
    ...(variant === 'soft' && {
      color: tokens.text,
      backgroundColor: tokens.bg,
      border: `1px solid ${tokens.border}`,
    }),
    ...(variant === 'outlined' && {
      backgroundColor: 'transparent',
      color: tokens.text,
      border: `1px solid ${tokens.border}`,
    }),
    ...(variant === 'filled' && {
      color: color === 'default' ? '#fff' : (theme.vars?.palette[color as keyof typeof theme.vars.palette] as any)?.contrastText,
      backgroundColor: color === 'default' ? theme.vars?.palette.text.primary : (theme.vars?.palette[color as keyof typeof theme.vars.palette] as any)?.main,
    }),
    ...(variant === 'inverted' && {
      color: tokens.text,
      backgroundColor: tokens.bg,
      border: `1px solid ${tokens.border}`,
    }),
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    cursor: 'default',
    // Brand book pill base
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.4,
    transition: theme.transitions.create('all', {
      duration: theme.transitions.duration.shorter,
    }),
    ...colorStyles,
  };
});
